import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { notifyXp } from '@/lib/xpToast';
import { RefreshCw, ArrowLeft, ArrowRight, CheckCircle2, X, Trophy, Sparkles } from 'lucide-react';

export default function ReframeBattle() {
  const { user } = useAuth();
  const [scenarios, setScenarios] = useState([]);
  const [round, setRound] = useState([]);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [phase, setPhase] = useState('ready');

  useEffect(() => {
    api.get('/games/reframe-scenarios').then(r => setScenarios(r.data || []));
  }, []);

  const startRound = () => {
    const shuffled = [...scenarios].sort(() => Math.random() - 0.5).slice(0, 5);
    setRound(shuffled); setIdx(0); setPicked(null); setScore(0); setCorrect(0); setPhase('playing');
  };

  const pick = (opt) => {
    if (picked) return;
    setPicked(opt);
    if (opt.score === 1) { setScore(s => s + 20); setCorrect(c => c + 1); }
  };

  const next = async () => {
    if (idx + 1 >= round.length) {
      setPhase('done');
      if (user) {
        try {
          // XP is computed server-side from `correct` count × admin-configured multiplier.
          const r = await api.post('/games/record', { game: 'reframe_battle', score, correct });
          notifyXp(r.data.xp_result, 'Reframe Battle');
          window.dispatchEvent(new Event('kc:xp-updated'));
        } catch {}
      }
      return;
    }
    setIdx(i => i + 1); setPicked(null);
  };

  const s = round[idx];

  return (
    <div className="bg-white min-h-screen">
      <div className="kc-container py-6">
        <Link to="/games" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-muted-kc hover:text-gold" data-testid="back-games"><ArrowLeft size={12}/> Games</Link>
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[color:var(--kc-gold-2)] font-semibold"><RefreshCw size={12}/> CBT in Disguise</div>
            <h1 className="font-display uppercase text-4xl md:text-5xl mt-2">Reframe Battle</h1>
          </div>
          {phase === 'playing' && <div className="text-sm text-muted-kc">Round {idx + 1} of {round.length} · Score: <span className="font-bold text-ink" data-testid="rb-score">{score}</span></div>}
        </div>

        {phase === 'ready' && (
          <div className="mt-8 max-w-2xl mx-auto text-center bg-surface-2 rounded-[var(--kc-radius-md)] p-8 border border-kc">
            <Sparkles className="mx-auto text-[color:var(--kc-gold)] mb-3" size={32}/>
            <h2 className="font-display uppercase text-3xl">5 real scenarios. Pick the strongest reframe.</h2>
            <p className="mt-3 text-ink-2">Each round you'll see a stressful moment and a thought your mind serves up. Choose the response that actually rewires the loop. Mick reacts to every pick.</p>
            <button onClick={startRound} className="mt-6 h-13 px-7 py-3 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold hover:bg-black/90" data-testid="rb-start">Start round <ArrowRight size={16}/></button>
          </div>
        )}

        {phase === 'playing' && s && (
          <div className="mt-6 max-w-3xl mx-auto">
            <div className="bg-ink text-white rounded-[var(--kc-radius-md)] p-6" data-testid="rb-card">
              <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold">The trigger</div>
              <div className="mt-1 text-lg">{s.trigger}</div>
              <div className="mt-4 text-[10px] tracking-[0.22em] uppercase text-white/60 font-semibold">Your mind says</div>
              <div className="mt-1 font-display uppercase text-2xl md:text-3xl text-red-300 leading-tight">“{s.thought}”</div>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {s.options.map((opt, i) => {
                const isPicked = picked === opt;
                const isCorrect = opt.score === 1;
                const showOutcome = !!picked;
                const cls = showOutcome
                  ? (isCorrect ? 'border-[color:var(--kc-success)] bg-green-50' : isPicked ? 'border-[color:var(--kc-danger)] bg-red-50' : 'border-kc opacity-60')
                  : 'border-kc-strong hover:border-[color:var(--kc-gold)] hover:bg-gold-soft';
                return (
                  <button key={i} onClick={() => pick(opt)} disabled={!!picked} className={`text-left p-4 rounded-[var(--kc-radius-md)] border-2 transition-all ${cls}`} data-testid={`rb-opt-${i}`}>
                    <div className="font-semibold text-ink leading-relaxed">“{opt.text}”</div>
                    {showOutcome && (
                      <div className="mt-2 flex items-start gap-2 text-sm text-ink-2">
                        {isCorrect ? <CheckCircle2 size={16} className="text-[color:var(--kc-success)] flex-shrink-0 mt-0.5"/> : <X size={16} className="text-[color:var(--kc-danger)] flex-shrink-0 mt-0.5"/>}
                        <span className="italic">{opt.feedback}</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {picked && (
              <div className="mt-5 flex justify-end">
                <button onClick={next} className="h-12 px-6 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold" data-testid="rb-next">{idx + 1 >= round.length ? 'Finish' : 'Next'} <ArrowRight size={16}/></button>
              </div>
            )}
          </div>
        )}

        {phase === 'done' && (
          <div className="mt-8 max-w-2xl mx-auto text-center bg-ink text-white rounded-[var(--kc-radius-md)] p-10">
            <Trophy size={48} className="mx-auto text-[color:var(--kc-gold)]"/>
            <h2 className="mt-4 font-display uppercase text-4xl">Round Done.</h2>
            <div className="mt-6 grid grid-cols-3 gap-6">
              <div><div className="font-display text-4xl text-[color:var(--kc-gold)]" data-testid="rb-final-score">{score}</div><div className="text-[10px] uppercase tracking-wider text-white/60">Score</div></div>
              <div><div className="font-display text-4xl">{correct}/{round.length}</div><div className="text-[10px] uppercase tracking-wider text-white/60">Correct</div></div>
              <div><div className="font-display text-4xl text-[color:var(--kc-gold)]">+{score}</div><div className="text-[10px] uppercase tracking-wider text-white/60">XP</div></div>
            </div>
            <div className="mt-6 flex justify-center gap-3">
              <button onClick={startRound} className="h-12 px-6 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold" data-testid="rb-again"><RefreshCw size={16}/> New round</button>
              <Link to="/games" className="h-12 px-6 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] border border-white/20 hover:bg-white/10 font-semibold">Other games</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
